// Button.tsx — app-wide button primitive (policy B, session panel first adopter).
// Variants × sizes × iconOnly; see docs/superpowers/specs/2026-07-05-session-panel-button-system-design.md
import React, { forwardRef } from "react";
import "./Button.css";

export type ButtonVariant = "primary" | "outline" | "ghost" | "danger";
export type ButtonSize = "md" | "sm";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Visual variant. Default: outline. */
  variant?: ButtonVariant;
  /** md = 30px, sm = 28px. Default: md. */
  size?: ButtonSize;
  /** Square icon button — aria-label is REQUIRED (dev-mode console.error if missing). */
  iconOnly?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "outline", size = "md", iconOnly = false, className, type, children, ...rest },
  ref,
) {
  if (process.env.NODE_ENV !== "production" && iconOnly && !rest["aria-label"]) {
    console.error("Button: iconOnly requires aria-label");
  }

  const cls = [
    "ui-btn",
    `ui-btn--${variant}`,
    size === "sm" ? "ui-btn--sm" : null,
    iconOnly ? "ui-btn--icon" : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button ref={ref} type={type ?? "button"} className={cls} {...rest}>
      {children}
    </button>
  );
});

export default Button;
